/**
 * hydrate-store-directory.js
 * ---
 * Custom element for the Stores A-Z / Store Directory page.
 *
 * Renders a filter/sort toolbar that mirrors the collection PLP experience —
 * pinned "Filter" + "Sort" pills open slide-in drawers, and committed
 * selections show as removable chips. Below the toolbar sits a paginated store
 * card grid (reuses renderStoreCard from ng-store-card) and prev/next
 * pagination.
 *
 * Filter model: the APIM store-search endpoint returns structured filter
 * definitions in `filters.regularFilters` (each entry typed by `filterType`:
 * `categoryList`, `storeType`, `alphaSort`, `sort`) with their available
 * options, and the currently applied filters in `filters.filtersApplied`.
 * Client state is a simple `_requestFilters` object (Record<string, string>)
 * whose keys are the API request keys ('storeTypeList', 'categoryList',
 * 'alphaSort') and whose values are the selected option value (identifier, or
 * categoryId for categories). Each value is wrapped in an array in the request
 * body (the API rejects scalar filter values with HTTP 400). After every fetch
 * the keys `filters.filtersApplied` no longer reports are dropped; the values
 * stay as sent, since the echo is the display value (see
 * `_syncRequestFiltersFromApplied`).
 *
 * Imports:
 *   - renderStoreCard / initStoreCardInteractions from @theme/ng-store-card
 *   - queryStoreList from @theme/query-store-list
 *   - getApiLocale from @theme/locale-map
 *   - htmlEscape / decodeHtmlEntities from @theme/utilities
 */

import { renderStoreCard, renderStoreCardSkeletons, initStoreCardInteractions } from "@theme/ng-store-card";
import { queryStoreList } from "@theme/query-store-list";
import { trackStoreSearch } from "@theme/hydrate-callers";
import { getStoreApiLocale } from "@theme/locale-map";
import { htmlEscape, decodeHtmlEntities } from "@theme/utilities";

/** @param {string} key @param {string} fallback @returns {string} */
const t = (key, fallback) => Theme?.translations?.[key] || fallback;

/** Browse default — the directory lists stores A → Z when there is no search term. */
const DEFAULT_SORT_VALUE = "STORE_NAME_A_TO_Z";

/** Search default — free-text results are ranked by relevance. */
const DEFAULT_SEARCH_SORT_VALUE = "BEST_MATCH";

/**
 * Maps a response `regularFilters[].filterType` to the key the API expects in
 * the request `filters` object (and reports back in `filtersApplied`). Store
 * Type is the one mismatch — the rail types it `storeType`, but the request /
 * applied key is `storeTypeList`; every other facet passes through unchanged.
 */
const REQUEST_KEY_BY_FILTER_TYPE = { storeType: "storeTypeList" };

/** @param {string} filterType @returns {string} */
const requestKeyFor = filterType => REQUEST_KEY_BY_FILTER_TYPE[filterType] || filterType;

/**
 * Extracts the value to send / select for one filter option. Category options
 * key off `categoryId`; every other facet uses `identifier` (falling back to
 * the display `value`). Note `filtersApplied[].selectedOptions[].value` echoes
 * the display value, not this one (see `_syncRequestFiltersFromApplied`).
 * @param {string} filterType @param {Record<string, any>} opt @returns {string}
 */
const optionValueFor = (filterType, opt) =>
  filterType === "categoryList" ? String(opt.categoryId ?? "") : String(opt.identifier ?? opt.value ?? "");

/** Chevron-left SVG for pagination prev button. */
const CHEVRON_LEFT = `<svg width="10" height="10" viewBox="0 0 10 10" fill="none" aria-hidden="true"><path d="M6.5 8L3.5 5L6.5 2" stroke="currentColor" stroke-width="1.25" stroke-linecap="round" stroke-linejoin="round"/></svg>`;

/** Chevron-right SVG for pagination next button. */
const CHEVRON_RIGHT = `<svg width="10" height="10" viewBox="0 0 10 10" fill="none" aria-hidden="true"><path d="M3.5 2L6.5 5L3.5 8" stroke="currentColor" stroke-width="1.25" stroke-linecap="round" stroke-linejoin="round"/></svg>`;

/* ---------------------------------------------------------------------------
 * Tailwind class strings for JS-rendered drawer markup. Kept in sync with the
 * collection PLP (`hydrate-filters.js`) so both pages render an identical
 * drawer. New tokens require `npm run build:css`.
 * ------------------------------------------------------------------------- */

/** Shared row geometry — sort + filter option rows. */
const ROW_CLS =
  "tw:flex tw:w-full tw:items-center tw:justify-between tw:gap-3 tw:lg:px-7 tw:lg:py-4 tw:px-5 tw:py-3 " +
  "tw:border-b tw:border-b-[rgb(var(--color-foreground-rgb)/0.20)] " +
  "tw:lg:text-[18px] tw:text-base tw:leading-[1.2] tw:cursor-pointer tw:hover:bg-[#ededed]";

/** Visually-hidden native input that drives the indicator via the peer pattern. */
const INPUT_CLS = "tw:peer tw:absolute tw:opacity-0 tw:pointer-events-none";

/** Mobile 16px / desktop 24px indicator with scaled checked fill. */
const BOX_CLS =
  "tw:relative tw:flex tw:items-center tw:justify-center tw:w-4 tw:h-4 tw:shrink-0 tw:lg:w-6 tw:lg:h-6 " +
  "tw:lg:rounded-full tw:rounded-full tw:border tw:border-[#D8D7D8] " +
  "tw:peer-checked:border-black tw:peer-checked:after:content-[''] tw:peer-checked:after:block " +
  "tw:peer-checked:after:w-2.5 tw:peer-checked:after:h-2.5 tw:peer-checked:after:rounded-full tw:peer-checked:after:bg-black " +
  "tw:lg:peer-checked:after:w-4 tw:lg:peer-checked:after:h-4 tw:lg:peer-checked:after:rounded-full " +
  "tw:peer-focus-visible:outline tw:peer-focus-visible:outline-2 tw:peer-focus-visible:outline-offset-2";

/** Radio reuses the box but turns circular with #211E24 fill on mobile. */
const RADIO_CLS =
  BOX_CLS +
  " tw:rounded-full tw:peer-checked:border-[#211E24] " +
  "tw:peer-checked:after:rounded-full tw:peer-checked:after:w-2 " +
  "tw:peer-checked:after:h-2 tw:peer-checked:after:bg-[#211E24] " +
  "tw:lg:rounded-full tw:lg:peer-checked:border-black " +
  "tw:lg:peer-checked:after:rounded-full tw:lg:peer-checked:after:w-4 " +
  "tw:lg:peer-checked:after:h-4 tw:lg:peer-checked:after:bg-black";

/**
 * Quick-filter pill button (JS-rendered, one per store-type facet option).
 * Outlined, transparent, rounded — identical to the collection PLP toolbar
 * pills (`hydrate-filters.js` PILL_CLS) so both pages read the same.
 */
const PILL_CLS =
  "tw:inline-flex tw:items-center tw:justify-center tw:h-7 tw:lg:h-[39px] tw:px-3 tw:lg:px-4 " +
  "tw:rounded-[70px] tw:border tw:border-[#CBCBCB] tw:bg-transparent " +
  "tw:text-[12px] tw:lg:text-[14px] tw:font-light tw:lg:font-medium tw:leading-[16px] tw:lg:leading-[18px] " +
  "tw:text-black tw:whitespace-nowrap tw:shrink-0 tw:cursor-pointer tw:font-[inherit] " +
  "tw:hover:bg-[#f5f5f5] tw:transition-colors";

/** Active state modifier — border turns black when the option is the committed selection. */
const PILL_ACTIVE_CLS = "tw:border-black";

/** Group navigation row (a full-width button) — row geometry + button reset. */
const GROUP_BTN_CLS =
  ROW_CLS +
  " tw:w-full tw:bg-transparent tw:border-x-0 tw:border-t-0 tw:text-start tw:select-none " +
  "tw:font-[inherit] tw:text-[var(--color-foreground)] tw:hover:bg-[#ededed]";

/**
 * Filter-by-letter button (JS-rendered, one per alphabet letter + "All" + any
 * numeric/symbol buckets). Compact square-ish outlined button.
 */
const ALPHA_CLS =
  "tw:inline-flex tw:items-center tw:justify-center tw:h-8 tw:min-w-8 tw:lg:h-9 tw:lg:min-w-9 tw:px-2 " +
  "tw:rounded-[8px] tw:border tw:text-[13px] tw:lg:text-[14px] tw:font-medium tw:leading-none " +
  "tw:cursor-pointer tw:font-[inherit] tw:transition-colors";

/**
 * Idle vs active appearance are mutually EXCLUSIVE class sets (never both on
 * one element) — stacking `tw:bg-black` on top of a base `tw:bg-transparent`
 * lets the equal-specificity transparent win by source order, so the selected
 * letter would render an invisible white glyph on a white fill.
 */
const ALPHA_IDLE_CLS = "tw:border-[#CBCBCB] tw:bg-transparent tw:text-black tw:hover:bg-[#f5f5f5]";

/** Active modifier — selected letter fills solid black with white glyph. */
const ALPHA_ACTIVE_CLS = "tw:border-black tw:bg-black tw:text-white tw:hover:bg-black";

/** Disabled modifier — letters with no matching stores are dimmed + inert. */
const ALPHA_DISABLED_CLS = "tw:opacity-30 tw:pointer-events-none";

/** A–Z, used to render the full alphabet grid regardless of which letters have stores. */
const ALPHABET = "ABCDEFGHIJKLMNOPQRSTUVWXYZ".split("");

/** Wrapper for the letter grid inside the drawer (padded to align with rows). */
const DRAWER_ALPHA_WRAP_CLS = "tw:px-5 tw:lg:px-7 tw:pt-4 tw:pb-5 tw:flex tw:flex-wrap tw:gap-1.5 tw:lg:gap-2";

class HydrateStoreDirectory extends HTMLElement {
  connectedCallback() {
    const ds = this.dataset;

    /** @type {number} */
    this._columnsDesktop = parseInt(ds.columnsDesktop || "5", 10);
    /** @type {number} */
    this._pageSize = parseInt(ds.pageSize || "25", 10);
    /** @type {number} */
    this._pageSizeMobile = parseInt(ds.pageSizeMobile || "10", 10);
    /** @type {string} — featured-stores link shown in the no-results state */
    this._featuredStoresUrl = ds.featuredStoresUrl || "";
    /** @type {string} */
    this._featuredStoresText = ds.featuredStoresText || "";
    /** @type {number} */
    this._currentPage = 1;
    /** @type {number} */
    this._totalCount = 0;
    /** @type {string} */
    this._activeSort = DEFAULT_SORT_VALUE;
    /** @type {string} Committed free-text search term ('' → '*' browse query). */
    this._searchQuery = "";

    /**
     * Filter params sent to the API on each fetch. Keys are the API request
     * keys ('storeTypeList', 'categoryList', 'alphaSort'); values are the
     * selected option value (identifier, or categoryId for categories). Each
     * value is wrapped in an array when the request body is built. This is
     * reconciled from `filters.filtersApplied` after every fetch so it mirrors
     * exactly what the API reports as applied. Empty object = no filters.
     * @type {Record<string, string>}
     */
    this._requestFilters = {};

    /**
     * Full filter definitions from the last API response's
     * `filters.regularFilters`. Each entry has `filterType`, `name`,
     * `filterOptions` (and `sortFilterOptions` for sort).
     * @type {Array<Object>}
     */
    this._regularFilters = [];

    /**
     * Applied filter state from the last API response's
     * `filters.filtersApplied`. Used to determine active/checked state in
     * the UI after each fetch.
     * @type {Array<Object>}
     */
    this._filtersApplied = [];

    /** @type {string} Staged sort while the sort drawer is open. */
    this._stagedSort = DEFAULT_SORT_VALUE;

    /** @type {'sort' | 'filter' | null} */
    this._activeDrawer = null;
    /** @type {HTMLElement | null} */
    this._lastTrigger = null;

    /**
     * The `filterType` of the group currently drilled into inside the filter
     * drawer ('categoryList' | 'storeType' | 'alphaSort'), or `null` to show
     * the 1st-level group list. Reset whenever the filter drawer closes.
     * @type {string | null}
     */
    this._activeFilterGroup = null;

    /** @type {AbortController | null} */
    this._controller = null;
    /** @type {MediaQueryList} */
    this._mobileQuery = window.matchMedia("(max-width: 767px)");

    // Sort options — hardcoded defaults, overridden when the API provides
    // sortFilterOptions in the sort entry of regularFilters.
    this._sortOptions = [
      { value: "STORE_NAME_A_TO_Z", label: ds.i18nSortAZ || "A – Z" },
      { value: "STORE_NAME_Z_TO_A", label: ds.i18nSortZA || "Z – A" },
      { value: "BEST_MATCH", label: ds.i18nSortBestMatch || "Best Match" },
      { value: "MOST_CASHBACK", label: ds.i18nSortMostCashback || "Most Cashback" },
    ];

    this._i18n = {
      clearFilters: ds.i18nClearFilters || "clear filters",
      results: ds.i18nResults || "Results",
      filterGroup: ds.i18nFilterGroup || "Store Type",
      categoryGroup: ds.i18nCategoryGroup || "Category",
      filterByLetter: ds.i18nFilterByLetter || "Filter by Letter:",
      sortLabel: ds.i18nSortGroup || "Sort",
      all: ds.i18nAlphaAll || "All",
    };

    /**
     * Facet group headings, keyed by the API's `regularFilters[].filterType`.
     * The APIM store-search response hardcodes English group names ("Category",
     * "Store Type", "Alpha Sort", "Sort") for EVERY languageCode — only the
     * options underneath are localized. So the theme locale is the source of
     * truth for headings and the API `name` is only a fallback for groups we
     * have no key for. Keyed explicitly so a new API group falls back to its
     * own name rather than silently inheriting "Category".
     */
    this._groupHeadings = {
      categoryList: this._i18n.categoryGroup,
      storeType: this._i18n.filterGroup,
      alphaSort: this._i18n.filterByLetter,
      sort: this._i18n.sortLabel,
    };

    // Restore applied page / sort / filter from the URL. All values apply
    // directly — no deferred pending pattern is needed since the new API
    // returns full filter options in every response regardless of applied
    // filters.
    const sp = new URLSearchParams(window.location.search);
    this._currentPage = parseInt(sp.get("page"), 10) || 1;
    const urlQuery = (sp.get("q") || "").trim();
    if (urlQuery) this._searchQuery = urlQuery;

    const urlSort = sp.get("sort");
    if (urlSort && this._sortOptions.some(opt => opt.value === urlSort)) {
      this._activeSort = urlSort;
      this._stagedSort = urlSort;
    } else if (this._searchQuery) {
      this._activeSort = DEFAULT_SEARCH_SORT_VALUE;
      this._stagedSort = DEFAULT_SEARCH_SORT_VALUE;
    }
    for (const key of ["storeTypeList", "categoryList", "alphaSort"]) {
      const value = (sp.get(key) || "").trim();
      if (value) this._requestFilters[key] = value;
    }
    // DOM refs — toolbar + grid live inside `this`; drawers move to the portal.
    this._grid = this.querySelector("[data-dir-grid]");
    this._pagination = this.querySelector("[data-dir-pagination]");
    this._status = this.querySelector("[data-dir-status]");
    this._toolbar = this.querySelector("[data-dir-toolbar]");
    this._skeletonToolbar = this.querySelector("[data-dir-skeleton]");
    this._resultsRow = this.querySelector("[data-dir-results-row]");
    this._resultCount = this.querySelector("[data-dir-result-count]");
    this._pillsContainer = this.querySelector("[data-dir-pills]");
    this._heading = this.querySelector("[data-dir-heading]");

    // Store search — a submit-on-enter form with a debounced live-search input
    // and a clear (×) button. Restore the term into the field on load.
    this._searchForm = this.querySelector("[data-dir-search-form]");
    this._searchInput = this.querySelector("[data-dir-search-input]");
    this._searchClear = this.querySelector("[data-dir-search-clear]");
    if (this._searchInput && this._searchQuery) {
      this._searchInput.value = this._searchQuery;
    }
    this._updateSearchClear();
    if (this._searchForm) {
      this._searchForm.addEventListener("submit", e => {
        e.preventDefault();
        this._applySearch(this._searchInput ? this._searchInput.value : "");
      });
    }
    if (this._searchInput) {
      // Only toggle the clear button while typing — the search itself runs on
      // submit (Enter / button click), not on every keystroke.
      this._searchInput.addEventListener("input", () => this._updateSearchClear());
    }
    if (this._searchClear) {
      this._searchClear.addEventListener("click", () => this._clearSearch());
    }

    // Relocate the drawer portal to <body> so fixed positioning resolves
    // against the viewport and the backdrop overlays the sticky header.
    this._portal = this.querySelector("[data-dir-portal]");
    if (this._portal) {
      this._portal.hidden = false;
      document.body.appendChild(this._portal);
      this._portal.addEventListener("change", e => this._handleChange(e));
      this._portal.addEventListener("click", e => this._handleClick(e));
    }

    // Event delegation for the toolbar triggers (still inside `this`).
    this.addEventListener("click", e => this._handleClick(e));

    // Escape closes the open drawer.
    this._onKeydown = e => {
      if (e.key === "Escape" && this._activeDrawer) this._closeActiveDrawer();
    };
    document.addEventListener("keydown", this._onKeydown);

    // URL filters/search apply directly to the initial fetch. The store-search
    // API returns the full filter catalog in every response regardless of the
    // applied filters (see note above), so no unfiltered pre-fetch is needed —
    // a filtered initial view is a single call, not two.
    this._fetchStores();
  }

  disconnectedCallback() {
    if (this._controller) this._controller.abort();
    if (this._onKeydown) document.removeEventListener("keydown", this._onKeydown);

    // Restore body scroll if a drawer was open.
    if (this._activeDrawer) {
      document.body.style.overflow = "";
      document.body.classList.remove("ngf-drawer-open");
    }

    // Remove the relocated portal from <body>.
    if (this._portal && this._portal.parentNode) {
      this._portal.parentNode.removeChild(this._portal);
    }
    this._portal = null;
  }

  /** @returns {number} Effective page size based on viewport. */
  get _effectivePageSize() {
    return this._mobileQuery.matches ? this._pageSizeMobile : this._pageSize;
  }

  /** Whether the storefront is viewed in the Shopify theme editor. */
  get _isDesignMode() {
    return Boolean(window.Shopify && window.Shopify.designMode);
  }

  /**
   * Queries inside the relocated portal (drawers/overlay). Falls back to `this`
   * before the portal is established.
   * @param {string} selector
   * @returns {HTMLElement | null}
   */
  _q(selector) {
    return (this._portal || this).querySelector(selector);
  }

  // ---------------------------------------------------------------------------
  // APIM fetch
  // ---------------------------------------------------------------------------

  /**
   * Builds the APIM `filters` request object from `_requestFilters`. The new
   * store-search contract requires each filter value to be an ARRAY — a scalar
   * string (the old shape) is rejected with HTTP 400. Keys are the API request
   * keys ('storeTypeList', 'categoryList', 'alphaSort').
   * @returns {Record<string, string[]>}
   */
  _buildApiFilters() {
    /** @type {Record<string, string[]>} */
    const out = {};
    for (const [key, value] of Object.entries(this._requestFilters)) {
      if (value) out[key] = [value];
    }
    return out;
  }

  /**
   * Writes the committed sort + filter + page state into the address bar so
   * the current view is shareable and survives a reload. Uses `replaceState`
   * to avoid flooding history. Params the component doesn't own (e.g.
   * `preview_theme_id`, `utm_*`) are preserved.
   */
  _syncUrl() {
    const url = new URL(window.location.href);
    const sp = url.searchParams;

    // Drop every param this component manages so a de-selected filter or a
    // return to the default sort / first page clears from the URL.
    const managed = new Set(["sort", "page", "storeTypeList", "categoryList", "alphaSort", "q"]);
    for (const key of [...sp.keys()]) {
      if (managed.has(key)) sp.delete(key);
    }

    // Re-add the active search term (omit when empty for clean URLs).
    if (this._searchQuery) {
      sp.set("q", this._searchQuery);
    }

    // Keep non-A → Z sorts in the URL. In particular, persisting BEST_MATCH
    // during a search ensures a reload restores the same relevance ordering.
    if (this._activeSort && this._activeSort !== DEFAULT_SORT_VALUE) {
      sp.set("sort", this._activeSort);
    }

    // Re-add the active filters.
    for (const [key, value] of Object.entries(this._requestFilters)) {
      if (value) sp.set(key, value);
    }

    // Re-add the page param only beyond page 1 (omit for page 1 for clean URLs).
    if (this._currentPage > 1) {
      sp.set("page", String(this._currentPage));
    }

    history.replaceState(history.state, "", url);
  }

  /**
   * Processes the `filters` object from the API response. Populates
   * `_regularFilters` with the full definitions and extracts sort options
   * from the sort entry's `sortFilterOptions`.
   * @param {Object|null} filters The `filters` object from the normalised response.
   */
  _parseRegularFilters(filters) {
    const regularFilters = Array.isArray(filters?.regularFilters) ? filters.regularFilters : [];
    this._regularFilters = regularFilters;

    // Extract sort options from the sort entry (if present); keep the
    // hardcoded defaults when the API doesn't provide them.
    const sortEntry = regularFilters.find(f => f.filterType === "sort");
    if (sortEntry && Array.isArray(sortEntry.sortFilterOptions) && sortEntry.sortFilterOptions.length > 0) {
      // The API's sort labels are hardcoded English regardless of languageCode
      // (same as the group names above), so a translated label seeded from the
      // theme locale in the constructor wins. The API is consulted only for
      // sort values the theme has no key for.
      const localizedByValue = new Map(this._sortOptions.map(opt => [opt.value, opt.label]));
      this._sortOptions = sortEntry.sortFilterOptions.map(opt => {
        const value = opt.identifier || opt.value || "";
        return {
          value,
          label: localizedByValue.get(value) || decodeHtmlEntities(String(opt.value || opt.identifier || "")),
        };
      });
    }
  }

  /**
   * Checks whether an option is present in the last API response's
   * `filtersApplied` array. Applied entries carry the API request key in
   * `filterType` and the selected value(s) nested in `selectedOptions[].value`.
   * @param {string} requestKey The API request key ('storeTypeList' etc.).
   * @param {string} value The option value (identifier / categoryId).
   * @returns {boolean}
   */
  _isFilterApplied(requestKey, value) {
    return this._filtersApplied.some(
      f =>
        f.filterType === requestKey &&
        Array.isArray(f.selectedOptions) &&
        f.selectedOptions.some(o => String(o.value) === String(value))
    );
  }

  /**
   * Reconciles `_requestFilters` with the API's `filtersApplied`: a key the API
   * reports as applied is kept, a key it dropped (stale / invalid value) falls
   * away. `sort` is excluded (handled via `_activeSort`).
   *
   * The applied entry's `selectedOptions[].value` is the option's DISPLAY value,
   * not its request value: on Taiwan the store-type filter sent as
   * "Partner Store" comes back as "合作商店" (the Chinese label; the entry's own
   * `identifier` is the filter key, not the option's). Adopting that echo put the
   * label in the URL and in the next request, and the API returned no stores
   * for it, so paging a filtered list past page 1 showed "No stores found". The
   * value we sent therefore stays the source of truth; the echo is only mapped
   * back through the option catalog when we hold nothing for that key.
   */
  _syncRequestFiltersFromApplied() {
    /** @type {Record<string, string>} */
    const next = {};
    for (const f of this._filtersApplied) {
      if (!f || f.filterType === "sort") continue;
      const opt = Array.isArray(f.selectedOptions) ? f.selectedOptions[0] : null;
      const echoed = opt && opt.value != null ? String(opt.value) : "";
      if (!echoed) continue;
      next[f.filterType] = this._requestFilters[f.filterType] || this._requestValueForEcho(f.filterType, echoed);
    }
    this._requestFilters = next;
  }

  /**
   * Maps a display value the API echoed in `filtersApplied` back to the value
   * the request needs, via the option catalog of the matching facet. Falls back
   * to the echo itself when no option matches.
   * @param {string} requestKey The API request key ('storeTypeList' etc.).
   * @param {string} echoed The echoed `selectedOptions[].value`.
   * @returns {string}
   */
  _requestValueForEcho(requestKey, echoed) {
    const entry = this._regularFilters.find(f => requestKeyFor(f.filterType) === requestKey);
    const options = Array.isArray(entry?.filterOptions) ? entry.filterOptions : [];
    const match = options.find(
      opt => optionValueFor(entry.filterType, opt) === echoed || String(opt.value ?? "") === echoed
    );
    return match ? optionValueFor(entry.filterType, match) : echoed;
  }

  /** Fetches stores from the APIM store-search endpoint and renders results. */
  async _fetchStores() {
    if (this._controller) this._controller.abort();
    this._controller = new AbortController();

    // Reflect the committed state in the URL (shareable + survives reload)
    // before firing the request — every re-fetch routes through here.
    this._syncUrl();

    this._showLoading();

    const locale = getStoreApiLocale();
    const pageSize = this._effectivePageSize;

    /** @type {Record<string, any>} */
    const params = {
      // The store-search API rejects an empty query with HTTP 400 — '*' is the
      // match-all browse query used when the shopper hasn't typed a search term.
      query: this._searchQuery || "*",
      page: this._currentPage,
      size: pageSize,
      sort: this._activeSort,
      signal: this._controller.signal,
      filters: this._buildApiFilters(),
      // Kept for backward compatibility — the endpoint may still use these.
      aggregations: ["storeTypeList", "alphaSort", "categoryList"],
      ...locale,
    };

    try {
      const result = await queryStoreList(params);

      // Every re-fetch here is a real store search — a typed term, a letter, a
      // facet or a page change — so each one reports. Emitted before the `ok`
      // check so a failed search is counted too, carrying its error message.
      trackStoreSearch(this._searchQuery || "", "store-directory", result);

      if (!result.ok) {
        this._showStatus(t("store_dir_error", "Something went wrong. Please try again."));
        this._clearPagination();
        return;
      }

      const stores = result.data?.stores || [];
      const totalCount = result.data?.totalCount || 0;
      const totalPages = result.data?.pagination?.totalPages || Math.ceil(totalCount / pageSize) || 1;

      // Parse filter definitions and applied state from every response.
      const filters = result.data?.filters;
      if (filters) {
        this._parseRegularFilters(filters);
        this._filtersApplied = Array.isArray(filters.filtersApplied) ? filters.filtersApplied : [];
        // Mirror client state to exactly what the API reports as applied.
        this._syncRequestFiltersFromApplied();
      }

      this._totalCount = totalCount;

      if (stores.length === 0) {
        this._showNoResults();
        this._clearGrid();
        this._clearPagination();
      } else {
        this._hideStatus();
        this._renderGrid(stores);
        this._renderPagination(this._currentPage, totalPages);
      }

      // Reveal the toolbar (chips + count reflect committed state).
      this._renderToolbar();

      if (this.hidden) this.hidden = false;
    } catch (error) {
      if (error instanceof DOMException && error.name === "AbortError") return;
      this._showStatus(t("store_dir_error", "Something went wrong. Please try again."));
      this._clearPagination();
    }
  }

  // ---------------------------------------------------------------------------
  // Grid + pagination
  // ---------------------------------------------------------------------------

  /**
   * Paints store-card skeletons into the grid while a fetch is in flight (marks
   * it aria-busy), replacing the plain "Loading stores…" text. Real cards or a
   * status message replace them once the request resolves. Loading visual only —
   * no behaviour change.
   */
  _showLoading() {
    if (!this._grid) return;
    this._hideStatus();
    // First load only: reserve the toolbar space with a skeleton until the real
    // toolbar renders. On filter/sort/page re-fetches the real toolbar is already
    // visible, so it stays put (matches the PLP filter skeleton behaviour).
    if (this._skeletonToolbar && this._toolbar && this._toolbar.hidden) {
      this._skeletonToolbar.hidden = false;
    }
    const count = Math.min(this._effectivePageSize || 10, 15);
    this._grid.setAttribute("aria-busy", "true");
    this._grid.innerHTML = renderStoreCardSkeletons(count);
    if (this.hidden) this.hidden = false;
  }

  /** @param {Array<Record<string, any>>} stores */
  _renderGrid(stores) {
    if (!this._grid) return;
    this._grid.innerHTML = stores.map(hit => renderStoreCard(hit)).join("");
    this._grid.removeAttribute("aria-busy");
    initStoreCardInteractions(this._grid);
  }

  _clearGrid() {
    if (this._grid) {
      this._grid.innerHTML = "";
      this._grid.removeAttribute("aria-busy");
    }
  }

  /**
   * @param {number} currentPage
   * @param {number} totalPages
   */
  _renderPagination(currentPage, totalPages) {
    if (!this._pagination) return;

    if (totalPages <= 1) {
      this._clearPagination();
      return;
    }

    const prevDisabled = currentPage <= 1;
    const nextDisabled = currentPage >= totalPages;

    this._pagination.innerHTML = `
      <button
        type="button"
        data-dir-prev
        class="store-dir__page-btn fs-pagination__prev"
        ${prevDisabled ? "disabled" : ""}
        aria-label="${t("store_dir_prev", "Previous page")}"
      >${CHEVRON_LEFT}</button>
      <span class="store-dir__page-indicator">${t("store_dir_page_indicator", "page {current} of {total}").replace("{current}", currentPage).replace("{total}", totalPages)}</span>
      <button
        type="button"
        data-dir-next
        class="store-dir__page-btn fs-pagination__next"
        ${nextDisabled ? "disabled" : ""}
        aria-label="${t("store_dir_next", "Next page")}"
      >${CHEVRON_RIGHT}</button>
    `;

    const prevBtn = this._pagination.querySelector("[data-dir-prev]");
    const nextBtn = this._pagination.querySelector("[data-dir-next]");

    if (prevBtn) {
      prevBtn.addEventListener("click", () => {
        if (this._currentPage > 1) {
          this._currentPage--;
          this._fetchStores();
          this._scrollToTop();
        }
      });
    }

    if (nextBtn) {
      nextBtn.addEventListener("click", () => {
        if (this._currentPage < totalPages) {
          this._currentPage++;
          this._fetchStores();
          this._scrollToTop();
        }
      });
    }
  }

  _clearPagination() {
    if (this._pagination) this._pagination.innerHTML = "";
  }

  // ---------------------------------------------------------------------------
  // Toolbar — count + chips (reflect committed state)
  // ---------------------------------------------------------------------------

  /** Reveals the toolbar and re-renders its dynamic parts. */
  _renderToolbar() {
    if (this._skeletonToolbar) this._skeletonToolbar.hidden = true;
    if (this._toolbar) this._toolbar.hidden = false;

    // Hide the Filter pill when no filterable entries exist (sort excluded).
    const filterTrigger = this.querySelector("[data-dir-open-filter]");
    const hasFilterableEntries = this._facetSource().some(f => f.filterType !== "sort");
    if (filterTrigger) filterTrigger.style.display = hasFilterableEntries ? "" : "none";

    if (this._pillsContainer) {
      this._pillsContainer.innerHTML = this._renderQuickFilterPills();
    }

    if (this._resultCount) {
      if (this._totalCount > 0) {
        this._resultCount.textContent = `${this._totalCount} ${this._i18n.results}`;
        this._resultCount.hidden = false;
      } else {
        this._resultCount.hidden = true;
      }
    }

    if (this._resultsRow) {
      this._resultsRow.hidden = !(this._totalCount > 0);
    }
  }

  /**
   * The facet definitions to render options from — always the live store-search
   * response (`_regularFilters`). Each filtered response already scopes its facet
   * counts + options to the active filter (MA-1900), so the toolbar/letter grid/
   * drawer show counts relevant to the current level rather than stale unfiltered
   * totals. Selected/active state comes from `_requestFilters`.
   * @returns {Array<Object>}
   */
  _facetSource() {
    return this._regularFilters;
  }

  /**
   * @param {Object} entry A `_facetSource()` entry.
   * @returns {string} Display name for a filter group. Resolved from the theme
   * locale via `_groupHeadings` (keyed by `filterType`); the API's `name` is a
   * fallback only for groups the theme has no key for. See `_groupHeadings` for
   * why the API name cannot be trusted for headings.
   */
  _groupHeading(entry) {
    const localized = this._groupHeadings[entry.filterType];
    if (localized) return localized;
    const rawDisplay = String(entry.name || "").trim();
    return rawDisplay ? decodeHtmlEntities(rawDisplay) : "";
  }

  /**
   * @returns {string} One pill per 1st-level filter group (category, alpha
   * sort, store type — sort excluded), matching the collection PLP toolbar.
   * A pill carries the black-border active class when its group currently has
   * an applied value. Clicking a pill opens the filter drawer directly on
   * that group's 2nd-level options.
   */
  _renderQuickFilterPills() {
    return this._facetSource()
      .filter(entry => entry.filterType !== "sort")
      .map(entry => {
        const requestKey = requestKeyFor(entry.filterType);
        const active = Boolean(this._requestFilters[requestKey]);
        const label = this._groupHeading(entry);
        return `<button class="${PILL_CLS}${active ? " " + PILL_ACTIVE_CLS : ""} fs-filters__group-toggle" data-dir-pill-group="${htmlEscape(entry.filterType)}" type="button" aria-pressed="${active ? "true" : "false"}">${htmlEscape(label)}</button>`;
      })
      .join("");
  }

  /**
   * @returns {string} The Filter-by-Letter bar: an "All" button, the full A–Z
   * alphabet (unavailable letters disabled), and any numeric/symbol buckets.
   * The active selection's button fills solid black.
   */
  _renderAlphaBar() {
    const alphaEntry = this._facetSource().find(f => f.filterType === "alphaSort");
    const options = Array.isArray(alphaEntry?.filterOptions) ? alphaEntry.filterOptions : [];

    // Build a map of available letters → API identifier values.
    /** @type {Map<string, string>} */
    const letterMap = new Map();
    /** @type {Array<{ label: string, value: string }>} */
    const extras = [];
    const seenExtra = new Set();

    for (const opt of options) {
      const raw = String(opt.identifier || opt.value || "").trim();
      if (!raw) continue;
      const count = typeof opt.count === "number" ? opt.count : 1;
      if (count === 0) continue;

      const first = raw.charAt(0).toUpperCase();
      if (first >= "A" && first <= "Z") {
        if (!letterMap.has(first)) letterMap.set(first, raw);
      } else if (!seenExtra.has(raw)) {
        seenExtra.add(raw);
        extras.push({ label: raw.toUpperCase(), value: raw });
      }
    }

    const selectedAlpha = this._requestFilters.alphaSort || "";

    /**
     * @param {string} label @param {string} value
     * @param {boolean} available @param {boolean} active
     */
    const mk = (label, value, available, active) => {
      const cls =
        ALPHA_CLS + " " + (active ? ALPHA_ACTIVE_CLS : ALPHA_IDLE_CLS) + (available ? "" : " " + ALPHA_DISABLED_CLS);
      const attrs = available ? `data-dir-alpha-value="${htmlEscape(value)}"` : 'aria-disabled="true" tabindex="-1"';
      return `<button class="${cls} fs-store-directory__letter" type="button" ${attrs} aria-pressed="${active ? "true" : "false"}">${htmlEscape(label)}</button>`;
    };

    const parts = [mk(this._i18n.all, "", true, selectedAlpha === "")];

    for (const letter of ALPHABET) {
      const key = letterMap.get(letter);
      const available = Boolean(key);
      parts.push(mk(letter, key || "", available, available && selectedAlpha === key));
    }

    for (const extra of extras) {
      parts.push(mk(extra.label, extra.value, true, selectedAlpha === extra.value));
    }

    return parts.join("");
  }

  /**
   * Applies a Filter-by-Letter selection. Selecting the active letter (or the
   * "All" button, value '') clears it; any other value replaces it.
   * @param {string} value Raw API identifier, or '' for All.
   */
  _setAlpha(value) {
    const current = this._requestFilters.alphaSort || "";
    const next = current === value ? "" : value;
    if (next === current) return;
    if (next === "") {
      delete this._requestFilters.alphaSort;
    } else {
      this._requestFilters.alphaSort = next;
    }
    this._currentPage = 1;
    this._renderToolbar();
    // Re-render just the drawer's letter grid so the active button updates
    // without disturbing the drawer's scroll position.
    if (this._activeDrawer === "filter") {
      const grid = this._q("[data-dir-alpha]");
      if (grid) grid.innerHTML = this._renderAlphaBar();
    }
    this._fetchStores();
  }

  // ---------------------------------------------------------------------------
  // Store search
  // ---------------------------------------------------------------------------

  /**
   * Commits a search term and refetches from page 1. Runs on submit (Enter /
   * button click) and on clear. No-ops when the trimmed term is unchanged.
   * A genuinely new term resets any applied filters (MA-1557) — otherwise a
   * filter picked for one search silently carries over and scopes the next.
   * @param {string} raw
   */
  _applySearch(raw) {
    const query = (raw || "").trim();
    this._updateSearchClear();
    if (query === this._searchQuery) return;
    this._searchQuery = query;
    this._activeSort = query ? DEFAULT_SEARCH_SORT_VALUE : DEFAULT_SORT_VALUE;
    this._stagedSort = this._activeSort;
    this._currentPage = 1;
    this._requestFilters = {};
    this._activeFilterGroup = null;
    this._fetchStores();
  }

  /** Clears the search field and returns to the unfiltered browse listing. */
  _clearSearch() {
    if (this._searchInput) this._searchInput.value = "";
    this._applySearch("");
    if (this._searchInput) this._searchInput.focus();
  }

  /** Shows the × clear button only when the field holds text. */
  _updateSearchClear() {
    if (!this._searchClear) return;
    const hasText = Boolean(this._searchInput && this._searchInput.value.trim());
    this._searchClear.hidden = !hasText;
  }

  // ---------------------------------------------------------------------------
  // Drawer bodies
  // ---------------------------------------------------------------------------

  /** Populates the sort drawer body with radio options from staged state. */
  _renderSortDrawerBody() {
    const body = this._q("[data-dir-sort-body]");
    if (!body) return;
    body.innerHTML = this._sortOptions
      .map(opt => {
        const checked = opt.value === this._stagedSort ? " checked" : "";
        return `<label class="${ROW_CLS} fs-sorting__option">
          <span>${htmlEscape(opt.label)}</span>
          <input type="radio" name="dir-sort" value="${htmlEscape(opt.value)}"${checked} data-dir-staged-sort class="${INPUT_CLS}">
          <span class="${RADIO_CLS}"></span>
        </label>`;
      })
      .join("");
  }

  /**
   * Populates the filter drawer body. Shows the 1st-level group list by
   * default (`_activeFilterGroup === null`), matching the collection PLP's
   * filter drawer; selecting a group swaps the body to that group's
   * 2nd-level options (letter grid for alphaSort, radio list otherwise) with
   * a back row. This page has a single drawer panel, so "opening a group"
   * swaps the body in place rather than sliding in a second panel.
   */
  _renderFilterDrawerBody() {
    const body = this._q("[data-dir-filter-body]");
    if (!body) return;

    if (this._activeFilterGroup) {
      body.innerHTML = this._renderFilterGroupOptions(this._activeFilterGroup);
      return;
    }

    body.innerHTML = this._facetSource()
      .filter(entry => entry.filterType !== "sort")
      .map(entry => this._renderFilterGroupNavRow(entry))
      .join("");
  }

  /**
   * @param {Object} entry A `_facetSource()` entry (sort excluded).
   * @returns {string} A nav row that drills into that group's 2nd-level
   * options when clicked.
   */
  _renderFilterGroupNavRow(entry) {
    return `<button class="${GROUP_BTN_CLS} fs-filters__group-toggle" type="button" data-dir-open-group="${htmlEscape(entry.filterType)}">
      <span class="tw:flex-1">${htmlEscape(this._groupHeading(entry))}</span>
      <svg class="tw:h-4 tw:w-4 tw:shrink-0 tw:lg:h-6 tw:lg:w-6" width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M9 18L15 12L9 6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>
    </button>`;
  }

  /**
   * @param {string} filterType The raw `filterType` of the drilled-into group.
   * @returns {string} A back row (returns to the group list) plus that one
   * group's 2nd-level options.
   */
  _renderFilterGroupOptions(filterType) {
    const entry = this._facetSource().find(f => f.filterType === filterType);
    if (!entry) return "";

    const back = `<button class="${GROUP_BTN_CLS} fs-filters__group-back" type="button" data-dir-back-to-groups>
      <svg class="tw:h-4 tw:w-4 tw:shrink-0 tw:lg:h-6 tw:lg:w-6" width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M15 18L9 12L15 6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>
      <span class="tw:flex-1">${htmlEscape(this._groupHeading(entry))}</span>
    </button>`;

    if (filterType === "alphaSort") {
      return (
        back +
        `<div class="${DRAWER_ALPHA_WRAP_CLS}" data-dir-alpha role="group" aria-label="${htmlEscape(this._i18n.filterByLetter)}">${this._renderAlphaBar()}</div>`
      );
    }

    const options = Array.isArray(entry.filterOptions) ? entry.filterOptions : [];
    const requestKey = requestKeyFor(filterType);
    const currentValue = this._requestFilters[requestKey] || "";

    const rows = [this._renderFilterRow(requestKey, "", this._i18n.all, currentValue === "")];
    for (const opt of options) {
      const optValue = optionValueFor(filterType, opt);
      const optLabel = decodeHtmlEntities(String(opt.value || opt.identifier || ""));
      const label = typeof opt.count === "number" ? `${optLabel} (${opt.count})` : optLabel;
      rows.push(this._renderFilterRow(requestKey, optValue, label, currentValue === optValue, optLabel));
    }

    return back + rows.join("");
  }

  /**
   * @param {string} filterType The `filterType` key (e.g. 'storeType', 'categoryList').
   * @param {string} value Raw option identifier ('' = the "All" / clear row).
   * @param {string} labelText Visible row label (may include a count).
   * @param {boolean} checked
   * @param {string} [labelAttr] Canonical label stored for reference (defaults to labelText).
   * @returns {string} A single-select filter row.
   */
  _renderFilterRow(filterType, value, labelText, checked, labelAttr) {
    return `<label class="${ROW_CLS} fs-filters__option">
      <span class="tw:flex-1">${htmlEscape(labelText)}</span>
      <input type="radio" name="dir-filter-${htmlEscape(filterType)}" data-dir-staged-filter="${htmlEscape(filterType)}" data-dir-label="${htmlEscape(labelAttr || labelText)}" value="${htmlEscape(value)}"${checked ? " checked" : ""} class="${INPUT_CLS}">
      <span class="${RADIO_CLS}"></span>
    </label>`;
  }

  // ---------------------------------------------------------------------------
  // Drawer management
  // ---------------------------------------------------------------------------

  _openSortDrawer() {
    if (this._activeDrawer) this._closeActiveDrawer();
    this._stagedSort = this._activeSort;
    this._renderSortDrawerBody();

    const drawer = this._q("[data-dir-sort-drawer]");
    const overlay = this._q("[data-dir-overlay]");
    if (drawer) {
      drawer.setAttribute("data-open", "");
      drawer.setAttribute("aria-hidden", "false");
    }
    if (overlay) overlay.setAttribute("data-open", "");
    document.body.style.overflow = "hidden";
    document.body.classList.add("ngf-drawer-open");
    this._activeDrawer = "sort";

    const firstInput = drawer?.querySelector('input[type="radio"]');
    if (firstInput) firstInput.focus();
  }

  _closeSortDrawer() {
    const drawer = this._q("[data-dir-sort-drawer]");
    const overlay = this._q("[data-dir-overlay]");
    if (drawer) {
      drawer.removeAttribute("data-open");
      drawer.setAttribute("aria-hidden", "true");
    }
    if (overlay) overlay.removeAttribute("data-open");
    document.body.style.overflow = "";
    document.body.classList.remove("ngf-drawer-open");
    this._activeDrawer = null;
    this._stagedSort = this._activeSort;
    this._restoreFocus();
  }

  /**
   * @param {string|null} [groupKey] When set (a top-bar pill click), opens
   * directly into that group's 2nd-level options instead of the group list.
   */
  _openFilterDrawer(groupKey = null) {
    if (this._activeDrawer) this._closeActiveDrawer();
    this._activeFilterGroup = groupKey;
    this._renderFilterDrawerBody();

    const drawer = this._q("[data-dir-filter-drawer]");
    const overlay = this._q("[data-dir-overlay]");
    if (drawer) {
      drawer.setAttribute("data-open", "");
      drawer.setAttribute("aria-hidden", "false");
    }
    if (overlay) overlay.setAttribute("data-open", "");
    document.body.style.overflow = "hidden";
    document.body.classList.add("ngf-drawer-open");
    this._activeDrawer = "filter";

    const closeBtn = drawer?.querySelector("[data-dir-close-filter]");
    if (closeBtn) closeBtn.focus();
  }

  _closeFilterDrawer() {
    const drawer = this._q("[data-dir-filter-drawer]");
    const overlay = this._q("[data-dir-overlay]");
    if (drawer) {
      drawer.removeAttribute("data-open");
      drawer.setAttribute("aria-hidden", "true");
    }
    if (overlay) overlay.removeAttribute("data-open");
    document.body.style.overflow = "";
    document.body.classList.remove("ngf-drawer-open");
    this._activeDrawer = null;
    // Always reopen at the group list — a drilled-into group shouldn't persist
    // across an unrelated close (Escape, overlay click, ×).
    this._activeFilterGroup = null;
    this._restoreFocus();
  }

  _closeActiveDrawer() {
    if (this._activeDrawer === "sort") {
      this._closeSortDrawer();
    } else if (this._activeDrawer === "filter") {
      this._closeFilterDrawer();
    }
  }

  _restoreFocus() {
    if (this._lastTrigger) {
      this._lastTrigger.focus();
      this._lastTrigger = null;
    }
  }

  // ---------------------------------------------------------------------------
  // Commit + clear
  // ---------------------------------------------------------------------------

  /** Commits staged sort and refetches. Drawer stays open. */
  _commitSort() {
    this._activeSort = this._stagedSort;
    this._currentPage = 1;
    this._renderToolbar();
    this._fetchStores();
  }

  /** Resets sort to the contextual default and applies immediately. */
  _clearSort() {
    const defaultSort = this._searchQuery ? DEFAULT_SEARCH_SORT_VALUE : DEFAULT_SORT_VALUE;
    this._stagedSort = defaultSort;
    this._activeSort = defaultSort;
    this._renderSortDrawerBody();
    this._currentPage = 1;
    this._renderToolbar();
    this._fetchStores();
  }

  /** Commits filter changes and refetches. Drawer stays open. */
  _commitFilters() {
    this._currentPage = 1;
    this._renderToolbar();
    this._fetchStores();
  }

  /**
   * Clears all drawer filters and applies immediately (closes the drawer).
   */
  _clearFilters() {
    this._requestFilters = {};
    this._closeActiveDrawer();
    this._currentPage = 1;
    this._renderToolbar();
    this._fetchStores();
  }

  // ---------------------------------------------------------------------------
  // Event handlers
  // ---------------------------------------------------------------------------

  /** @param {Event} event */
  _handleChange(event) {
    const target = /** @type {HTMLElement} */ (event.target);

    // Sort radio — commit immediately, then close the drawer (MA-1419: since
    // selections apply with no Apply step, the open drawer only hides the
    // updated results).
    if (target.matches("[data-dir-staged-sort]")) {
      this._stagedSort = /** @type {HTMLInputElement} */ (target).value;
      this._commitSort();
      this._closeActiveDrawer();
      return;
    }

    // Filter radio — update _requestFilters and commit immediately, then close
    // the drawer (MA-1419). The "All" row carries an empty value and clears
    // that filterType's selection.
    if (target.matches("[data-dir-staged-filter]")) {
      const input = /** @type {HTMLInputElement} */ (target);
      const filterType = input.getAttribute("data-dir-staged-filter") || "";
      if (input.value === "") {
        delete this._requestFilters[filterType];
      } else {
        this._requestFilters[filterType] = input.value;
      }
      this._commitFilters();
      this._closeActiveDrawer();
      return;
    }
  }

  /** @param {Event} event */
  _handleClick(event) {
    const target = /** @type {HTMLElement} */ (event.target).closest(
      "[data-dir-open-sort], [data-dir-open-filter], [data-dir-close-sort], [data-dir-close-filter], " +
        "[data-dir-clear-sort], [data-dir-clear-filter], " +
        "[data-dir-pill-group], [data-dir-open-group], [data-dir-back-to-groups], " +
        "[data-dir-alpha-value], [data-dir-overlay]"
    );
    if (!target) return;

    if (target.hasAttribute("data-dir-open-sort")) {
      this._lastTrigger = target;
      this._openSortDrawer();
      return;
    }

    if (target.hasAttribute("data-dir-open-filter")) {
      this._lastTrigger = target;
      this._openFilterDrawer();
      return;
    }

    if (target.hasAttribute("data-dir-close-sort")) {
      this._closeSortDrawer();
      return;
    }

    if (target.hasAttribute("data-dir-close-filter")) {
      this._closeFilterDrawer();
      return;
    }

    if (target.hasAttribute("data-dir-clear-sort")) {
      this._clearSort();
      return;
    }

    if (target.hasAttribute("data-dir-clear-filter")) {
      this._clearFilters();
      return;
    }

    // Toolbar group pill — open the filter drawer directly on that group's
    // 2nd-level options (bypasses the group list).
    if (target.hasAttribute("data-dir-pill-group")) {
      this._lastTrigger = target;
      this._openFilterDrawer(target.getAttribute("data-dir-pill-group") || "");
      return;
    }

    // Group-list nav row — drill into that group's 2nd-level options.
    if (target.hasAttribute("data-dir-open-group")) {
      this._activeFilterGroup = target.getAttribute("data-dir-open-group") || "";
      this._renderFilterDrawerBody();
      return;
    }

    // Back row inside a drilled-into group — return to the group list.
    if (target.hasAttribute("data-dir-back-to-groups")) {
      this._activeFilterGroup = null;
      this._renderFilterDrawerBody();
      return;
    }

    // Filter-by-letter button — apply that letter directly. The letter grid is
    // also a selection, so a click made inside the open Filter drawer closes it
    // (MA-1419); the guard keeps any non-drawer rendering of the grid inert.
    if (target.hasAttribute("data-dir-alpha-value")) {
      this._setAlpha(target.getAttribute("data-dir-alpha-value") || "");
      if (this._activeDrawer) this._closeActiveDrawer();
      return;
    }

    // Overlay click closes whichever drawer is open.
    if (target.hasAttribute("data-dir-overlay")) {
      this._closeActiveDrawer();
    }
  }

  // ---------------------------------------------------------------------------
  // Status helpers
  // ---------------------------------------------------------------------------

  /** @param {string} msg */
  _showStatus(msg) {
    // Error / no-results paths land here — drop the loading skeleton toolbar too.
    if (this._skeletonToolbar) this._skeletonToolbar.hidden = true;
    if (!this._status) return;
    this._status.textContent = msg;
    this._status.hidden = false;
  }

  /**
   * No-results variant of {@link _showStatus}: shows the "no stores found"
   * message and, when configured, a clickable link to the Featured Stores page
   * so the shopper has a way forward. Built with DOM nodes (not innerHTML) so
   * the merchant-supplied link text/URL can't inject markup.
   */
  _showNoResults() {
    if (this._skeletonToolbar) this._skeletonToolbar.hidden = true;
    if (!this._status) return;
    this._status.textContent = "";
    this._status.appendChild(
      document.createTextNode(t("store_dir_no_results", "No stores found for your search"))
    );
    if (this._featuredStoresUrl && this._featuredStoresText) {
      this._status.appendChild(document.createElement("br"));
      const link = document.createElement("a");
      link.className = "store-dir__status-link fs-store-directory__featured-link";
      link.href = this._featuredStoresUrl;
      link.textContent = this._featuredStoresText;
      this._status.appendChild(link);
    }
    this._status.hidden = false;
  }

  _hideStatus() {
    if (this._status) this._status.hidden = true;
  }

  _scrollToTop() {
    this.scrollIntoView({ behavior: "smooth", block: "start" });
  }
}

if (!customElements.get("hydrate-store-directory")) {
  customElements.define("hydrate-store-directory", HydrateStoreDirectory);
}
